// const DEFAULT_EXPORT_FILE_NAME = "dyslexa-text.txt"
import { Document, Packer, Paragraph, PageBreak, Footer, TextRun, AlignmentType, PageNumber, ShadingType } from 'docx'
import { PAGE_BREAK_TOKEN } from './constants'
import { breakSentencesAtAbbreviationSafePeriods } from './sentence-breaks'
import { saveFileToDevice } from './save-file'

/** The type the share sheet is asked about, the save dialog filters on, and the download claims. */
const DOCX_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'

/**
 * Builds the export file name: dyslexa-YYYY-MM-DD-HH-MM-SS.docx
 *
 * Shown to the user in the confirmation message, which is the only way anyone
 * knows what to look for in Downloads.
 */
function buildDocxFileName() {
    const now = new Date()
    const year = now.getFullYear()
    const month = String(now.getMonth() + 1).padStart(2, '0')
    const day = String(now.getDate()).padStart(2, '0')
    const hours = String(now.getHours()).padStart(2, '0')
    const minutes = String(now.getMinutes()).padStart(2, '0')
    const seconds = String(now.getSeconds()).padStart(2, '0')
    return `dyslexa-${year}-${month}-${day}-${hours}-${minutes}-${seconds}.docx`
}


/**
 * Maps CSS font-family strings to DOCX-compatible font names.
 * Dyslexa uses custom fonts (Lexend, Atkinson, Comic Neue) that may not be available
 * on all systems, so we use widely-available fallbacks.
 * 
 * @param {string} fontFamily - CSS font-family string (e.g., 'var(--font-lexend), Verdana, sans-serif')
 * @returns {string} DOCX-compatible font name
 */
function mapFontFamilyForDocx(fontFamily) {
    if (!fontFamily) return 'Arial'
    
    const fontLower = fontFamily.toLowerCase()
    
    // Map Dyslexa's custom fonts to widely-available alternatives
    if (fontLower.includes('lexend')) {
        // Lexend is designed for readability, Verdana is a good fallback
        return 'Verdana'
    }
    if (fontLower.includes('atkinson')) {
        // Atkinson Hyperlegible, use Verdana as fallback
        return 'Verdana'
    }
    if (fontLower.includes('comic')) {
        // Legacy Comic selection – map to a standard readable font
        return 'Verdana'
    }
    if (fontLower.includes('verdana')) {
        return 'Verdana'
    }
    if (fontLower.includes('tahoma')) {
        return 'Tahoma'
    }
    if (fontLower.includes('trebuchet')) {
        return 'Trebuchet MS'
    }
    if (fontLower.includes('arial')) {
        return 'Arial'
    }
    if (fontLower.includes('calibri')) {
        return 'Calibri'
    }
    if (fontLower.includes('open sans')) {
        return 'Open Sans'
    }
    
    // Default fallback
    return 'Arial'
}

/**
 * Converts CSS hex color to DOCX format (without #).
 * 
 * @param {string} cssColor - CSS hex color (e.g., '#1A1A1A')
 * @returns {string} DOCX color format (e.g., '1A1A1A')
 */
function convertColorForDocx(cssColor) {
    if (!cssColor) return '000000'
    return cssColor.replace('#', '').toUpperCase()
}

/**
 * Converts CSS px font size to DOCX half-points.
 * DOCX uses half-points (1pt = 2 half-points).
 * Approximate conversion: 1px ≈ 0.75pt, so halfPoints ≈ fontSize * 1.5
 * 
 * @param {number} fontSizePx - Font size in pixels
 * @returns {number} Font size in DOCX half-points
 */
function convertFontSizeForDocx(fontSizePx) {
    if (!fontSizePx) return 22 // Default 11pt
    // Round to nearest integer for cleaner values
    return Math.round(fontSizePx * 1.5)
}

/**
 * Calculates character spacing from em value for DOCX.
 * DOCX uses twips (1/20 of a point) for character spacing.
 * 
 * @param {number} fontSizePx - Font size in pixels
 * @param {number} letterSpacingEm - Letter spacing in em (e.g., 0.04)
 * @returns {number} Character spacing in twips
 */
function calculateCharacterSpacing(fontSizePx, letterSpacingEm = 0.04) {
    // Convert px to pt (1px ≈ 0.75pt)
    const fontSizePt = fontSizePx * 0.75
    // Calculate em in points, then convert to twips (20 twips per pt)
    return Math.round(fontSizePt * letterSpacingEm * 20)
}

/**
 * Applies sentence-per-line formatting to text.
 * Respects Polish abbreviations to avoid incorrect line breaks.
 * 
 * @param {string} text - Text to format
 * @param {boolean} newSentenceNewLine - Whether to add newlines after sentences
 * @returns {string} Formatted text
 */
function formatTextForExport(text, newSentenceNewLine) {
    if (!newSentenceNewLine) return text

    // Shared with the text display screen so export and screen agree, and so
    // neither builds a lookbehind assertion (unsupported before Safari 16.4).
    let formatted = breakSentencesAtAbbreviationSafePeriods(text)

    // Also handle ! and ? as sentence endings
    formatted = formatted.replace(/!\s+/g, "!\n")
    formatted = formatted.replace(/\?\s+/g, "?\n")

    return formatted
}

/**
 * Line types for DOCX formatting
 * @typedef {'mainHeading' | 'subHeading' | 'bullet' | 'text'} LineType
 */

/**
 * Parsed line result
 * @typedef {Object} ParsedLine
 * @property {LineType} type - The type of line content
 * @property {string} content - The extracted text content without markers
 */

/**
 * Parses a line to detect its type and extract content.
 * Detects headings (## ##, ### ###) and bullet points (* ).
 * 
 * @param {string} line - The line to parse
 * @returns {ParsedLine} The parsed line with type and content
 */
function parseTextLine(line) {
    // Subheading: ### text ### (check before main heading to avoid partial matches)
    const subHeadingMatch = line.match(/^###\s*(.+?)\s*###$/)
    if (subHeadingMatch) {
        return { type: 'subHeading', content: subHeadingMatch[1].trim() }
    }
    
    // Main heading: ## text ##
    const mainHeadingMatch = line.match(/^##\s*(.+?)\s*##$/)
    if (mainHeadingMatch) {
        return { type: 'mainHeading', content: mainHeadingMatch[1].trim() }
    }
    
    // Bullet point: * text (asterisk followed by space)
    const bulletMatch = line.match(/^\*\s+(.+)$/)
    if (bulletMatch) {
        return { type: 'bullet', content: bulletMatch[1].trim() }
    }
    
    // Regular text
    return { type: 'text', content: line }
}


/**
 * Turn one line into runs, making **emphasis** actually bold instead of literal.
 *
 * The export wrote `parsedLine.content` straight into a single TextRun, so a saved
 * document contained the asterisks - `**załóż okulary ochronne**` in the middle of
 * a sentence, in a file the reader had just been shown correctly on screen. The
 * reader has understood these markers since emphasis shipped; the export never
 * learned about them, and no test covered it.
 *
 * An unpaired marker is left exactly as it is rather than guessed at, which is the
 * same rule the reader and the editor follow.
 *
 * @param {string} content
 * @param {{font: string, size: number, color: string, characterSpacing: number}} fontConfig
 * @param {{sizeMultiplier?: number, bold?: boolean}} [options]
 * @returns {TextRun[]}
 */
function buildTextRuns(content, fontConfig, options = {}) {
    const size = options.sizeMultiplier
        ? Math.round(fontConfig.size * options.sizeMultiplier)
        : fontConfig.size
    const baseBold = options.bold === true

    const run = (text, bold) => new TextRun({
        text,
        font: fontConfig.font,
        size,
        color: fontConfig.color,
        characterSpacing: fontConfig.characterSpacing,
        bold: bold || baseBold,
    })

    if (!content.includes('**')) return [run(content, false)]

    return content
        .split(/(\*\*[^*]+\*\*)/g)
        .filter(chunk => chunk !== '')
        .map(chunk => {
            const match = chunk.match(/^\*\*([^*]+)\*\*$/)
            return match ? run(match[1], true) : run(chunk, false)
        })
}

/**
 * Exports given text into the file in DOCX format with user's display settings applied.
 * 
 * @param {string} text - Text to export.
 * @param {Object} settings - User's text display settings (optional).
 * @param {string} settings.fontFamily - CSS font-family string.
 * @param {number} settings.fontSize - Font size in pixels.
 * @param {number} settings.lineSpacing - Line spacing as percentage (e.g., 150 for 150%).
 * @param {string} settings.textColor - Text color in CSS hex format (e.g., '#1A1A1A').
 * @param {string} settings.backgroundColor - Background color in CSS hex format (e.g., '#FAF3E0').
 * @param {boolean} settings.newSentenceNewLine - Whether each sentence should be on a new line.
 * @returns {Promise<{outcome: 'shared'|'saved'|'downloaded'|'cancelled', filename: string}>}
 *   Resolves once the file has been handed to the device; rejects if it could not be.
 */
export async function exportTextToFile(text, settings = {}) {
    return exportTextToDocxFile(text, settings);
}

/**
 * Builds a DOCX file with the given text and styling and hands it to the device.
 *
 * Awaited all the way out, and it used to be fire-and-forget: nothing awaited the
 * packing, nothing caught a failure, and the caller marked the text as saved
 * either way. DYS-027.
 * 
 * @param {string} text - Text to export.
 * @param {Object} settings - User's text display settings.
 * @returns {Promise<{outcome: 'shared'|'saved'|'downloaded'|'cancelled', filename: string}>}
 */
async function exportTextToDocxFile(text, settings = {}) {
    // Default settings matching Dyslexa defaults
    const {
        fontFamily = 'Arial',
        fontSize = 14,
        lineSpacing = 150,
        textColor = '#1A1A1A',
        backgroundColor = '#FAF3E0',
        newSentenceNewLine = false,
    } = settings
    
    // Convert settings to DOCX format
    const docxFont = mapFontFamilyForDocx(fontFamily)
    const docxFontSize = convertFontSizeForDocx(fontSize)
    const docxTextColor = convertColorForDocx(textColor)
    const docxBgColor = convertColorForDocx(backgroundColor)
    const characterSpacing = calculateCharacterSpacing(fontSize, 0.04) // Fixed 0.04em letter-spacing
    
    // Calculate line spacing in twips (1 point = 20 twips)
    // DOCX line spacing is specified in twips when using lineRule
    // For percentage-based: line spacing value = (line height ratio) * 240
    // 240 twips = single spacing (100%)
    const lineSpacingValue = Math.round((lineSpacing / 100) * 240)
    
    // Font configuration for all text
    const fontConfig = {
        font: docxFont,
        size: docxFontSize,
        color: docxTextColor,
        characterSpacing: characterSpacing,
    }
    
    // Apply sentence-per-line formatting if enabled
    const formattedText = formatTextForExport(text, newSentenceNewLine)
    
    // Sanitize text for export:
    // - remove form feed characters that some pipelines might use for page breaks
    const sanitizedText = formattedText.replace(/\f/g, '')
    
    // Split text by page break tokens and create separate paragraphs for each section
    const sections = sanitizedText.split(PAGE_BREAK_TOKEN)
    const paragraphs = []
    
    // Paragraph styling with background color and line spacing
    const paragraphOptions = {
        shading: {
            type: ShadingType.CLEAR, // Solid color fill
            fill: docxBgColor,
            color: 'auto',
        },
        spacing: {
            line: lineSpacingValue,
        },
        alignment: AlignmentType.LEFT,
    }
    
    sections.forEach((section, index) => {
        // Split each section by newlines and create paragraphs.
        // Every newline from the editor becomes its own paragraph, including empty lines.
        const lines = section.split('\n')
        lines.forEach(line => {
            // Normalize potential CRLF endings
            const normalizedLine = line.replace(/\r$/, '')
            const trimmed = normalizedLine.trim()
            
            if (trimmed === '') {
                // Preserve explicit blank lines as empty paragraphs
                paragraphs.push(
                    new Paragraph({
                        ...paragraphOptions,
                        children: [
                            new TextRun({
                                text: '',
                                font: fontConfig.font,
                                size: fontConfig.size,
                                color: fontConfig.color,
                                characterSpacing: fontConfig.characterSpacing,
                            }),
                        ],
                    })
                )
                return
            }
            
            // Parse line to detect type (heading, bullet, regular text)
            const parsedLine = parseTextLine(trimmed)
            
            // Create appropriate paragraph based on line type
            switch (parsedLine.type) {
                case 'mainHeading':
                    // Main heading: bold, 1.4x larger font size
                    paragraphs.push(
                        new Paragraph({
                            ...paragraphOptions,
                            spacing: {
                                ...paragraphOptions.spacing,
                                before: 240, // Extra space before heading (12pt)
                                after: 120,  // Space after heading (6pt)
                            },
                            children: buildTextRuns(parsedLine.content, fontConfig, { sizeMultiplier: 1.4, bold: true }),
                        })
                    )
                    break
                    
                case 'subHeading':
                    // Subheading: bold, 1.2x larger font size
                    paragraphs.push(
                        new Paragraph({
                            ...paragraphOptions,
                            spacing: {
                                ...paragraphOptions.spacing,
                                before: 180, // Extra space before subheading (9pt)
                                after: 80,   // Space after subheading (4pt)
                            },
                            children: buildTextRuns(parsedLine.content, fontConfig, { sizeMultiplier: 1.2, bold: true }),
                        })
                    )
                    break
                    
                case 'bullet':
                    // Bullet point: add bullet symbol with indentation
                    paragraphs.push(
                        new Paragraph({
                            ...paragraphOptions,
                            indent: {
                                left: 360, // 0.25 inch left indent (in twips)
                                hanging: 180, // Hanging indent for bullet (in twips)
                            },
                            children: [
                                new TextRun({
                                    text: '• ', // Bullet symbol
                                    font: fontConfig.font,
                                    size: fontConfig.size,
                                    color: fontConfig.color,
                                }),
                                ...buildTextRuns(parsedLine.content, fontConfig),
                            ],
                        })
                    )
                    break
                    
                default:
                    // Regular text
                    paragraphs.push(
                        new Paragraph({
                            ...paragraphOptions,
                            children: buildTextRuns(parsedLine.content, fontConfig),
                        })
                    )
            }
        })
        
        // Add a page break paragraph after each section (except the last one)
        if (index < sections.length - 1) {
            paragraphs.push(new Paragraph({ 
                ...paragraphOptions,
                children: [new PageBreak()] 
            }))
        }
    })
    
    // Create footer with page numbers in bottom right corner for multi-page documents only.
    // Footer uses same font settings but without background color.
    // We approximate page count by the number of logical sections (PAGE_BREAK_TOKEN splits).
    const logicalPageCount = sections.length
    const hasMultiplePages = logicalPageCount > 1
    
    const footer = hasMultiplePages
        ? new Footer({
            children: [
                new Paragraph({
                    alignment: AlignmentType.RIGHT,
                    children: [
                        new TextRun({
                            text: 'Strona ',
                            font: fontConfig.font,
                            size: fontConfig.size,
                            color: fontConfig.color,
                        }),
                        new TextRun({
                            children: [PageNumber.CURRENT],
                            font: fontConfig.font,
                            size: fontConfig.size,
                            color: fontConfig.color,
                        }),
                    ],
                }),
            ],
        })
        : undefined
    
    const doc = new Document({
        sections: [
            {
                footers: footer
                    ? {
                        default: footer,
                    }
                    : {},
                children: paragraphs,
            },
        ],
    })

    const blob = await Packer.toBlob(doc)
    const filename = buildDocxFileName()
    const outcome = await saveFileToDevice(blob, filename, DOCX_MIME_TYPE)

    return { outcome, filename }
}
